import type { BillingStructure } from '@/types';
import type { UserConfigurationState } from '@/types/userConfiguration';
/** Remove or reset values that belong to *disabled* toggles */
export function sanitizeUserConfig(cfg: UserConfigurationState): UserConfigurationState {
  const copy = { ...cfg };

  // ── Multipliers Billing Structure Sanitization ──────────────────────
  const roles = copy.roles || [];
  let uniformBillingStructure: BillingStructure | null = null;
  if (roles.length > 0) {
    const firstStructure = roles[0].billingStructure;
    if (firstStructure && roles.every(r => r.billingStructure === firstStructure)) {
      uniformBillingStructure = firstStructure;
    }
  }

  if (uniformBillingStructure && copy.rateMultipliers) {
    copy.rateMultipliers = copy.rateMultipliers.map(multiplier => {
      const newMultiplier = { ...multiplier };

      if (newMultiplier.defaultPerShiftThresholdHours !== null && newMultiplier.defaultPerShiftThresholdHours !== undefined) {
        newMultiplier.perShiftBillingStructures = [uniformBillingStructure];
      } else {
        newMultiplier.perShiftBillingStructures = null;
      }

      if (newMultiplier.defaultPerWeekThresholdHours !== null && newMultiplier.defaultPerWeekThresholdHours !== undefined) {
        newMultiplier.perWeekBillingStructures = [uniformBillingStructure];
      } else {
        newMultiplier.perWeekBillingStructures = null;
      }

      return newMultiplier;
    });
  }

  // ── Travel ──────────────────────────────────────────
  if (!copy.needsTravel) {
    Object.assign(copy, {
      perDiemBaseRate: null,
      travelDayRateMultiplier: null,
      multiplierAppliesToOverages: false,
      usesGsaRate: false,
      applyPerDiem: false,
    });
    // Clear travel-specific expense policy when travel is disabled
    if (copy.expenseConfig?.expensePolicies?.travel_policy) {
      copy.expenseConfig = {
        ...copy.expenseConfig,
        expensePolicies: {
          ...copy.expenseConfig.expensePolicies,
          travel_policy: undefined,
        },
      };
    }
  } else {
    if (!copy.applyPerDiem) {
      copy.usesGsaRate = false;
      copy.perDiemBaseRate = null;
    }
  }

  // ── Half-Day billing ────────────────────────────────
  const anyRoleAllowsHalfDay = copy.roles?.some(r => r.billingStructure === 'day_rate' && r.allowHalfDay);
  if (!anyRoleAllowsHalfDay) {
    copy.billingStructureConfig.halfDayHours = null;
  }

  // ── Premium-shift rules ─────────────────────────────
  if (!copy.allowPremiumShifts) {
    copy.premiumShiftOffsetMode        = null;
    copy.premiumShiftFixedOffset       = null;
    copy.premiumShiftMultiplierCode    = null;
  }

  // ── Short-turn surcharge ────────────────────────────────
  if (!copy.usesShortTurn) {
    copy.shortTurnConfig = null;
  } else if (copy.shortTurnConfig) { // If it's enabled and the config exists
    const { surchargeType } = copy.shortTurnConfig;
    if (surchargeType !== 'fixed_dollar' && surchargeType !== 'hourly_premium') {
      copy.shortTurnConfig.shortTurnFixedSurchargeAmount = null;
    }
    // Preset normalizer (matches SQL preset_validity_check):
    //   carry_over      → floor_source='previous_shift', overtime_offset=0
    //   bump_up         → floor_source='previous_shift', overtime_offset=1
    //   offset_overtime → floor_source='none',           overtime_offset >= 1 (preserve if ≥1, else 1)
    //   guaranteed_rate → floor_source='fixed_tier',     overtime_offset=0, floor_tier_code OR custom_value
    //   hourly_premium  → floor_source='none',           overtime_offset=0
    //   fixed_dollar    → floor_source='none',           overtime_offset=0
    switch (surchargeType) {
      case 'carry_over':
        copy.shortTurnConfig.floorSource = 'previous_shift';
        copy.shortTurnConfig.overtimeOffset = 0;
        copy.shortTurnConfig.floorTierCode = null;
        copy.shortTurnConfig.floorTierCustomValue = null;
        break;
      case 'bump_up':
        copy.shortTurnConfig.floorSource = 'previous_shift';
        copy.shortTurnConfig.overtimeOffset = 1;
        copy.shortTurnConfig.floorTierCode = null;
        copy.shortTurnConfig.floorTierCustomValue = null;
        break;
      case 'offset_overtime':
        copy.shortTurnConfig.floorSource = 'none';
        if (!copy.shortTurnConfig.overtimeOffset || copy.shortTurnConfig.overtimeOffset < 1) {
          copy.shortTurnConfig.overtimeOffset = 1;
        }
        copy.shortTurnConfig.floorTierCode = null;
        copy.shortTurnConfig.floorTierCustomValue = null;
        break;
      case 'guaranteed_rate':
        copy.shortTurnConfig.floorSource = 'fixed_tier';
        copy.shortTurnConfig.overtimeOffset = 0;
        // mutual exclusion: if both populated, prefer floor_tier_code
        if (copy.shortTurnConfig.floorTierCode && copy.shortTurnConfig.floorTierCustomValue != null) {
          copy.shortTurnConfig.floorTierCustomValue = null;
        }
        break;
      default:
        // hourly_premium / fixed_dollar / null → preset-defaults
        copy.shortTurnConfig.floorSource = 'none';
        copy.shortTurnConfig.overtimeOffset = 0;
        copy.shortTurnConfig.floorTierCode = null;
        copy.shortTurnConfig.floorTierCustomValue = null;
    }
    if (surchargeType !== 'hourly_premium') {
      copy.shortTurnConfig.premiumType = null;
      copy.shortTurnConfig.premiumValue = null;
      copy.shortTurnConfig.premiumBasis = 'base_rate';
    }
  }

  // ── Auto-Send Reminders ─────────────────────────────
  // If auto-send reminders is disabled, clear any configured reminder days.
  if (!copy.autoSendReminders) {
    copy.reminderDays = [];
  }

  // ── Late-fee rules ─────────────────────────────────────
  if (!copy.useLateFee) {
    copy.lateFeePercentage        = null;
    copy.lateFeeFixedAmount       = null;
    copy.lateFeeType              = null as unknown as typeof copy.lateFeeType;
    copy.recurringLateFee         = 'once';
    copy.recurringLateFeeInterval = null;
  } else if (copy.recurringLateFee !== 'interval') {
    copy.recurringLateFeeInterval = null;   // only keep interval when actually used
  }

  // ── Early-payment discount rules ──────────────────────
  if (!copy.useEarlyPaymentDiscount) {
    copy.earlyPaymentDiscountPercentage = null;
    copy.earlyPaymentFixedAmount        = null;
    copy.earlyPaymentType               = null as unknown as typeof copy.earlyPaymentType;
    copy.earlyPaymentDays               = null;
  } else if (copy.earlyPaymentType === "percentage") {
    copy.earlyPaymentFixedAmount = null;            // inactive field ⇒ clear
  } else if (copy.earlyPaymentType === "fixed") {
    copy.earlyPaymentDiscountPercentage = null;     // inactive field ⇒ clear
  }

  // ── Cancellation policy rules ─────────────────────────
  if (!copy.useCancellationPolicy) {
    copy.cancellationPolicy = [];
  }

  // ── Holiday premium rules ─────────────────────────
  if (!copy.countryCodes || copy.countryCodes.length === 0) {
    copy.countryCodes = ['US'];
  }
  if (!copy.applyHolidayPremium) {
    copy.holidayPremiumType = 'offset_overtime'; // Reset to default
    copy.holidayFixedPremiumAmount = null;
    copy.holidayHourlyPremiumType = null;
    copy.holidayHourlyPremiumValue = null;
    copy.holidayPremiumBasis = 'base_rate';
    copy.holidayApplicableRoleIds = null;
    copy.holidayApplyOnHalfDays = true;
    // holidayCatalogIds and holidayCustomIds are preserved across toggle — user selections
    // should not be wiped when the premium toggle is turned off temporarily.
  } else {
    // Preset normalizer (matches SQL preset_validity_check for holiday):
    //   offset_overtime → floor_source='none',       overtime_offset >= 1
    //   guaranteed_rate → floor_source='fixed_tier', overtime_offset=0, code OR custom
    //   hourly_premium  → floor_source='none',       overtime_offset=0
    //   fixed_dollar    → floor_source='none',       overtime_offset=0
    switch (copy.holidayPremiumType) {
      case 'offset_overtime':
        copy.holidayFloorSource = 'none';
        if (!copy.holidayOvertimeOffset || copy.holidayOvertimeOffset < 1) {
          copy.holidayOvertimeOffset = 1;
        }
        copy.holidayFloorTierCode = null;
        copy.holidayFloorTierCustomValue = null;
        break;
      case 'guaranteed_rate':
        copy.holidayFloorSource = 'fixed_tier';
        copy.holidayOvertimeOffset = 0;
        if (copy.holidayFloorTierCode && copy.holidayFloorTierCustomValue != null) {
          copy.holidayFloorTierCustomValue = null;
        }
        break;
      default:
        // hourly_premium / fixed_dollar / null → preset-defaults
        copy.holidayFloorSource = 'none';
        copy.holidayOvertimeOffset = 0;
        copy.holidayFloorTierCode = null;
        copy.holidayFloorTierCustomValue = null;
    }
    if (copy.holidayPremiumType !== 'fixed_dollar') {
      copy.holidayFixedPremiumAmount = null;
    }
    if (copy.holidayPremiumType !== 'hourly_premium') {
      copy.holidayHourlyPremiumType = null;
      copy.holidayHourlyPremiumValue = null;
      copy.holidayPremiumBasis = 'base_rate';
    }
  }

  // ── Meal Break rules ───────────────────────────
  if (copy.mealConfig) {
    // Migrate legacy value
    if (copy.mealConfig.premiumBasis === 'base_rate') {
      copy.mealConfig.premiumBasis = 'STRAIGHT_TIME';
    }

    if (!copy.mealConfig.applyMealSurcharge) {
      copy.mealConfig.mealSurchargeType = null;
      copy.mealConfig.premiumType = null;
      copy.mealConfig.premiumValue = null;
      copy.mealConfig.creditIntervalMinutes = null;
      copy.mealConfig.timeCreditMinutes = null;
      copy.mealConfig.fixedFeeSchedule = null;
      copy.mealConfig.premiumBasis = 'STRAIGHT_TIME';
      copy.mealConfig.premiumStacking = 'flat';
      copy.mealConfig.applyOnHalfDays = true;
      copy.mealConfig.applicableRoleIds = null;
    } else {
      switch (copy.mealConfig.mealSurchargeType) {
        case 'credit_extra_time':
          // credit_extra_time uses premiumBasis for tier selection — preserve it
          copy.mealConfig.premiumType = null;
          copy.mealConfig.premiumValue = null;
          copy.mealConfig.premiumStacking = 'flat';
          break;
        case 'offset_overtime':
        case 'fixed_fee':
          copy.mealConfig.premiumType = null;
          copy.mealConfig.premiumValue = null;
          copy.mealConfig.premiumBasis = 'STRAIGHT_TIME';
          copy.mealConfig.premiumStacking = 'flat';
          break;
      }

      // Preset normalizer for meal unified recipe (matches SQL preset_validity_check):
      //   offset_overtime → floor_source='none',       overtime_offset >= 1
      //   guaranteed_rate → floor_source='fixed_tier', overtime_offset=0, code OR custom
      //   other methods   → floor_source='none',       overtime_offset=0
      switch (copy.mealConfig.mealSurchargeType) {
        case 'offset_overtime':
          copy.mealConfig.floorSource = 'none';
          if (!copy.mealConfig.overtimeOffset || copy.mealConfig.overtimeOffset < 1) {
            copy.mealConfig.overtimeOffset = 1;
          }
          copy.mealConfig.floorTierCode = null;
          copy.mealConfig.floorTierCustomValue = null;
          break;
        case 'guaranteed_rate':
          copy.mealConfig.floorSource = 'fixed_tier';
          copy.mealConfig.overtimeOffset = 0;
          if (copy.mealConfig.floorTierCode && copy.mealConfig.floorTierCustomValue != null) {
            copy.mealConfig.floorTierCustomValue = null;
          }
          break;
        default:
          copy.mealConfig.floorSource = 'none';
          copy.mealConfig.overtimeOffset = 0;
          copy.mealConfig.floorTierCode = null;
          copy.mealConfig.floorTierCustomValue = null;
      }
    }
  }

  // ── Overnight Premium rules ───────────────────────────
  if (copy.overnightConfig) {
    if (!copy.overnightConfig.applyOvernightPremium) {
      copy.overnightConfig.overnightMethod = null;
      copy.overnightConfig.thresholdEnabled = false;
      copy.overnightConfig.thresholdType = null;
      copy.overnightConfig.percentageThreshold = null;
      copy.overnightConfig.hoursThreshold = null;
      copy.overnightConfig.premiumType = null;
      copy.overnightConfig.premiumValue = null;
      copy.overnightConfig.flatRateAmount = null;
      copy.overnightConfig.timeCreditMinutes = null;
      copy.overnightConfig.applyOnHalfDays = true;
      copy.overnightConfig.premiumBasis = 'base_rate';
      copy.overnightConfig.applicableRoleIds = null;
    } else {
      // Handle threshold settings
      if (copy.overnightConfig.thresholdEnabled) {
        if (copy.overnightConfig.thresholdType === 'percentage') {
          copy.overnightConfig.hoursThreshold = null;
        } else if (copy.overnightConfig.thresholdType === 'hours') {
          copy.overnightConfig.percentageThreshold = null;
        } else { // if type is somehow null, disable it
          copy.overnightConfig.thresholdEnabled = false;
          copy.overnightConfig.percentageThreshold = null;
          copy.overnightConfig.hoursThreshold = null;
        }
      } else {
        copy.overnightConfig.thresholdType = null;
        copy.overnightConfig.percentageThreshold = null;
        copy.overnightConfig.hoursThreshold = null;
      }

      switch (copy.overnightConfig.overnightMethod) {
        case 'hourly_premium':
          copy.overnightConfig.flatRateAmount = null;
          copy.overnightConfig.timeCreditMinutes = null;
          break;
        case 'flat_rate':
          copy.overnightConfig.premiumType = null;
          copy.overnightConfig.premiumValue = null;
          copy.overnightConfig.premiumBasis = 'base_rate';
          copy.overnightConfig.timeCreditMinutes = null;
          break;
        case 'offset_overtime':
          copy.overnightConfig.premiumType = null;
          copy.overnightConfig.premiumValue = null;
          copy.overnightConfig.flatRateAmount = null;
          copy.overnightConfig.timeCreditMinutes = null;
          copy.overnightConfig.premiumBasis = 'base_rate';
          break;
        case 'credit_extra_time':
          copy.overnightConfig.premiumType = null;
          copy.overnightConfig.premiumValue = null;
          copy.overnightConfig.premiumBasis = 'base_rate';
          copy.overnightConfig.flatRateAmount = null;
          break;
        default: // overnightMethod is null or something else, clear all
          copy.overnightConfig.premiumType = null;
          copy.overnightConfig.premiumValue = null;
          copy.overnightConfig.flatRateAmount = null;
          copy.overnightConfig.timeCreditMinutes = null;
          copy.overnightConfig.premiumBasis = 'base_rate';
          break;
      }

      // Preset normalizer for overnight unified recipe (matches SQL preset_validity_check):
      //   offset_overtime → floor_source='none',       overtime_offset >= 1
      //   guaranteed_rate → floor_source='fixed_tier', overtime_offset=0, code OR custom
      //   other methods   → floor_source='none',       overtime_offset=0
      switch (copy.overnightConfig.overnightMethod) {
        case 'offset_overtime':
          copy.overnightConfig.floorSource = 'none';
          if (!copy.overnightConfig.overtimeOffset || copy.overnightConfig.overtimeOffset < 1) {
            copy.overnightConfig.overtimeOffset = 1;
          }
          copy.overnightConfig.floorTierCode = null;
          copy.overnightConfig.floorTierCustomValue = null;
          break;
        case 'guaranteed_rate':
          copy.overnightConfig.floorSource = 'fixed_tier';
          copy.overnightConfig.overtimeOffset = 0;
          if (copy.overnightConfig.floorTierCode && copy.overnightConfig.floorTierCustomValue != null) {
            copy.overnightConfig.floorTierCustomValue = null;
          }
          break;
        default:
          copy.overnightConfig.floorSource = 'none';
          copy.overnightConfig.overtimeOffset = 0;
          copy.overnightConfig.floorTierCode = null;
          copy.overnightConfig.floorTierCustomValue = null;
      }
    }
  }

  // ── Late-fee mutually-exclusive fields ────────────────
  if (copy.useLateFee) {
    if (copy.lateFeeType === "percentage") {
      copy.lateFeeFixedAmount = null;
    } else if (copy.lateFeeType === "fixed") {
      copy.lateFeePercentage = null;
    }
  }

  return copy;
}
